import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform, useSpring, useInView } from 'framer-motion';
import { FaArrowRight, FaPlay, FaMusic, FaLanguage, FaGraduationCap } from 'react-icons/fa';
import gsap from 'gsap';

const Hero: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const heroRef = useRef<HTMLDivElement>(null);
  const carouselRef = useRef<HTMLDivElement>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isCarouselInView, setIsCarouselInView] = useState(false);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"]
  });

  // Parallax transforms with spring physics
  const backgroundY = useSpring(useTransform(scrollYProgress, [0, 1], ["0%", "50%"]));
  const textY = useSpring(useTransform(scrollYProgress, [0, 1], ["0%", "100%"]));
  const overlayOpacity = useSpring(useTransform(scrollYProgress, [0, 0.5], [0.4, 0.8]));

  // Carousel data with your provided images
  const carouselItems = [
    {
      id: 1,
      image: "https://kcgdwjmwmfciiiryhagj.supabase.co/storage/v1/object/public/englishupload/Screenshot%202025-08-11%20203222.png",
      title: "Interactive Song Learning",
      description: "Learn English through your favorite songs with real-time lyrics, translations, and vocabulary explanations.",
      icon: <FaMusic className="w-8 h-8" />,
      gradient: "from-red-500 to-red-700"
    },
    {
      id: 2,
      image: "https://kcgdwjmwmfciiiryhagj.supabase.co/storage/v1/object/public/englishupload/Screenshot%202025-08-11%20203257.png",
      title: "AI-Powered Practice",
      description: "Engage with intelligent exercises that adapt to your learning pace and provide instant feedback.",
      icon: <FaGraduationCap className="w-8 h-8" />,
      gradient: "from-purple-500 to-purple-700"
    },
    {
      id: 3,
      image: "https://kcgdwjmwmfciiiryhagj.supabase.co/storage/v1/object/public/englishupload/Screenshot%202025-08-11%20204130.png",
      title: "Immersive Stories",
      description: "Dive into captivating stories with interactive translations and cultural context explanations.",
      icon: <FaLanguage className="w-8 h-8" />,
      gradient: "from-blue-500 to-blue-700"
    },
    {
      id: 4,
      image: "https://kcgdwjmwmfciiiryhagj.supabase.co/storage/v1/object/public/englishupload/Screenshot%202025-08-11%20205120.png",
      title: "Progress Tracking",
      description: "Monitor your learning journey with detailed analytics and personalized insights.",
      icon: <FaPlay className="w-8 h-8" />,
      gradient: "from-green-500 to-green-700"
    },
    {
      id: 5,
      image: "https://kcgdwjmwmfciiiryhagj.supabase.co/storage/v1/object/public/englishupload/Screenshot%202025-08-11%20205145.png",
      title: "Community Learning",
      description: "Connect with fellow learners and share your progress in our vibrant learning community.",
      icon: <FaMusic className="w-8 h-8" />,
      gradient: "from-orange-500 to-orange-700"
    }
  ];

  // Auto-advance carousel
  useEffect(() => {
    if (!isCarouselInView) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % carouselItems.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [isCarouselInView, carouselItems.length]);

  // Intersection observer for carousel
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsCarouselInView(entry.isIntersecting);
      },
      { threshold: 0.3 }
    );

    if (carouselRef.current) {
      observer.observe(carouselRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div ref={containerRef} className="relative">
      {/* Parallax Hero Section */}
      <section ref={heroRef} className="relative h-screen overflow-hidden">
        {/* Parallax Background */}
        <motion.div 
          className="absolute inset-0 z-0"
          style={{ y: backgroundY }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-black to-red-950"></div>
          
          {/* Animated Grid Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0 bg-grid-pattern"></div>
          </div>
          
          {/* Floating Particles */}
          {[...Array(50)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-1 h-1 bg-red-400/30 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [0, -100, 0],
                opacity: [0.3, 1, 0.3],
                scale: [1, 1.5, 1]
              }}
              transition={{
                duration: 3 + Math.random() * 2,
                repeat: Infinity,
                delay: Math.random() * 2,
                ease: "easeInOut"
              }}
            />
          ))}
        </motion.div>

        {/* Dark Overlay with Parallax */}
        <motion.div 
          className="absolute inset-0 bg-black z-10"
          style={{ opacity: overlayOpacity }}
        />

        {/* Hero Content */}
        <motion.div 
          className="relative z-20 h-full flex items-center justify-center px-6 pt-20"
          style={{ y: textY }}
        >
          <div className="max-w-6xl mx-auto text-center">
            {/* Glassmorphism Main Card */}
            <motion.div
              className="relative bg-white/5 backdrop-blur-xl rounded-3xl border border-white/10 p-8 md:p-12 shadow-2xl"
              initial={{ opacity: 0, scale: 0.9, y: 50 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 1, ease: "easeOut" }}
            >
              {/* Animated Background Glow */}
              <div className="absolute inset-0 bg-gradient-to-r from-red-500/10 via-transparent to-red-500/10 rounded-3xl animate-pulse"></div>
              
              {/* Content */}
              <div className="relative z-10">
                <motion.p
                  className="text-red-400 font-medium mb-4 tracking-wider flex items-center justify-center"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                >
                  <span className="inline-block w-8 h-[2px] bg-red-400 mr-3"></span>
                  POWERED BY AI · INSPIRED BY MUSIC
                  <span className="inline-block w-8 h-[2px] bg-red-400 ml-3"></span>
                </motion.p>
                
                <motion.h1 
                  className="text-5xl md:text-7xl lg:text-8xl font-extrabold mb-6 leading-tight"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 1, delay: 0.4 }}
                >
                  <span className="text-white">Master English with</span>
                  <br />
                  <span className="bg-gradient-to-r from-red-400 via-red-300 to-red-200 bg-clip-text text-transparent drop-shadow-[0_5px_20px_rgba(239,68,68,0.3)]">
                    Musical Intelligence
                  </span>
                </motion.h1>
                
                <motion.p 
                  className="text-xl md:text-2xl text-white/80 mb-8 max-w-3xl mx-auto leading-relaxed"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.6 }}
                >
                  Experience the future of language learning through AI-powered music analysis, 
                  interactive exercises, and personalized feedback that adapts to your learning style.
                </motion.p>
                
                <motion.div
                  className="flex flex-wrap justify-center gap-6"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.8 }}
                >
                  <Link 
                    to="/register" 
                    className="group relative inline-flex items-center bg-gradient-to-r from-red-500 to-red-600 text-white px-8 py-4 rounded-2xl font-bold text-lg hover:from-red-400 hover:to-red-500 transform hover:scale-105 transition-all duration-300 shadow-[0_10px_40px_rgba(239,68,68,0.3)] overflow-hidden"
                  >
                    <span className="absolute inset-0 bg-gradient-to-r from-red-400 to-red-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                    <span className="relative flex items-center">
                      Start Learning Free
                      <motion.div
                        className="ml-3"
                        animate={{ x: [0, 5, 0] }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                      >
                        <FaArrowRight />
                      </motion.div>
                    </span>
                  </Link>
                  
                  <button 
                    onClick={() => {
                      document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="inline-flex items-center px-8 py-4 border-2 border-red-500/50 rounded-2xl text-white hover:bg-red-500/10 transition-all duration-300 backdrop-blur-sm"
                  >
                    <FaPlay className="mr-3" />
                    See How It Works
                  </button>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div 
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-30"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.2 }}
        >
          <motion.div
            className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center"
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <motion.div
              className="w-1 h-3 bg-red-400 rounded-full mt-2"
              animate={{ y: [0, 6, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
          </motion.div>
        </motion.div>
      </section>

      {/* Horizontal Scrolling Carousel Section */}
      <section ref={carouselRef} className="relative min-h-screen bg-gradient-to-br from-black via-gray-900 to-black overflow-hidden" id="how-it-works">
        {/* Section Header */}
        <div className="relative z-20 pt-20 pb-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto px-6"
          >
            <h2 className="text-4xl md:text-6xl font-bold mb-6 text-white">
              How It <span className="bg-gradient-to-r from-red-400 to-red-600 bg-clip-text text-transparent">Works</span>
            </h2>
            <p className="text-xl md:text-2xl text-white/70 max-w-3xl mx-auto">
              Discover the revolutionary way to learn English through AI-powered music analysis
            </p>
          </motion.div>
        </div>

        {/* Carousel Container */}
        <div className="relative h-[600px] md:h-[700px] overflow-hidden">
          {/* Background Images with Parallax */}
          {carouselItems.map((item, index) => (
            <motion.div
              key={item.id}
              className="absolute inset-0"
              initial={{ x: "100%" }}
              animate={{ 
                x: index === currentSlide ? "0%" : index < currentSlide ? "-100%" : "100%",
                scale: index === currentSlide ? 1 : 0.9
              }}
              transition={{ 
                duration: 1.2, 
                ease: [0.25, 0.46, 0.45, 0.94]
              }}
            >
              {/* Image with Overlay */}
              <div className="relative w-full h-full">
                <img 
                  src={item.image}
                  alt={item.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent"></div>
              </div>

              {/* Content Overlay */}
              <div className="absolute inset-0 flex items-center">
                <div className="max-w-7xl mx-auto px-6 w-full">
                  <div className="max-w-2xl">
                    {/* Glassmorphism Content Card */}
                    <motion.div
                      className="bg-white/10 backdrop-blur-xl rounded-3xl border border-white/20 p-8 md:p-12 shadow-2xl"
                      initial={{ opacity: 0, x: -100, scale: 0.9 }}
                      animate={{ 
                        opacity: index === currentSlide ? 1 : 0,
                        x: index === currentSlide ? 0 : -100,
                        scale: index === currentSlide ? 1 : 0.9
                      }}
                      transition={{ 
                        duration: 0.8, 
                        delay: index === currentSlide ? 0.3 : 0,
                        ease: "easeOut"
                      }}
                    >
                      {/* Icon with Gradient Background */}
                      <motion.div 
                        className={`inline-flex items-center justify-center w-16 h-16 md:w-20 md:h-20 bg-gradient-to-r ${item.gradient} rounded-2xl mb-6 shadow-lg`}
                        whileHover={{ scale: 1.1, rotate: 5 }}
                        transition={{ duration: 0.3 }}
                      >
                        <div className="text-white">
                          {item.icon}
                        </div>
                      </motion.div>

                      <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
                        {item.title}
                      </h3>
                      
                      <p className="text-lg md:text-xl text-white/80 leading-relaxed mb-8">
                        {item.description}
                      </p>

                      <motion.button
                        className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl font-semibold hover:from-red-400 hover:to-red-500 transition-all duration-300 shadow-lg"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        Learn More
                        <FaArrowRight className="ml-2" />
                      </motion.button>
                    </motion.div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}

          {/* Carousel Navigation */}
          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-30">
            <div className="flex space-x-3">
              {carouselItems.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index === currentSlide 
                      ? 'bg-red-400 scale-125' 
                      : 'bg-white/30 hover:bg-white/50'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={() => setCurrentSlide((prev) => (prev - 1 + carouselItems.length) % carouselItems.length)}
            className="absolute left-6 top-1/2 transform -translate-y-1/2 z-30 w-12 h-12 bg-white/10 backdrop-blur-lg rounded-full border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-all duration-300"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <button
            onClick={() => setCurrentSlide((prev) => (prev + 1) % carouselItems.length)}
            className="absolute right-6 top-1/2 transform -translate-y-1/2 z-30 w-12 h-12 bg-white/10 backdrop-blur-lg rounded-full border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-all duration-300"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </section>

      {/* Custom Styles */}
      <style jsx>{`
        .bg-grid-pattern {
          background-image: 
            linear-gradient(rgba(239, 68, 68, 0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(239, 68, 68, 0.1) 1px, transparent 1px);
          background-size: 50px 50px;
          animation: grid-move 20s linear infinite;
        }
        
        @keyframes grid-move {
          0% { transform: translate(0, 0); }
          100% { transform: translate(50px, 50px); }
        }
        
        @keyframes float-glow {
          0%, 100% { 
            transform: scale(1) rotate(0deg);
            opacity: 0.7;
          }
          50% { 
            transform: scale(1.1) rotate(180deg);
            opacity: 1;
          }
        }
        
        .animate-float-glow {
          animation: float-glow 4s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default Hero;